'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Sheet — right-slide entity-edit panel.
 * Figma: Design System / Compositions / Sheet (242:671), 3 size variants.
 *
 * Use Sheet for entity create/edit/view forms (Member, Household, Yahrzeit,
 * User, Invitation). Use Dialog for destructive confirmations. See
 * docs/DESIGN_SYSTEM.md § "Modal pattern".
 */

const Sheet = DialogPrimitive.Root
const SheetTrigger = DialogPrimitive.Trigger
const SheetClose = DialogPrimitive.Close
const SheetPortal = DialogPrimitive.Portal

const SheetOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            'fixed inset-0 z-40 bg-[var(--bg-inverse)]/40 backdrop-blur-[2px]',
            'data-[state=open]:animate-overlay-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            'motion-reduce:animate-none',
            className,
        )}
        {...props}
    />
))
SheetOverlay.displayName = 'SheetOverlay'

const sheetVariants = cva(
    [
        'fixed right-0 top-0 z-50 flex h-dvh flex-col',
        'bg-[var(--bg-surface)] border-l border-[var(--border-subtle)]',
        'max-w-[100vw]',
        'data-[state=open]:animate-slide-in-right data-[state=closed]:animate-slide-out-right',
        'motion-reduce:animate-none',
        'focus:outline-none',
    ],
    {
        variants: {
            size: {
                sm: 'w-100',
                md: 'w-140',
                lg: 'w-180',
            },
        },
        defaultVariants: {
            size: 'md',
        },
    },
)

type SheetContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> &
    VariantProps<typeof sheetVariants>

const SheetContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    SheetContentProps
>(({ className, children, size, onOpenAutoFocus, onCloseAutoFocus, ...props }, ref) => {
    // Most sheets are opened programmatically (no Radix Trigger), so Radix has
    // nothing to restore focus to on close and drops it on <body>. When
    // onOpenAutoFocus fires, focus still sits on whatever the user activated —
    // capture it there (NOT at mount: the wrapper renders while closed) and
    // restore it ourselves. focus({preventScroll}) also stops the
    // open-autofocus from scroll-jumping the page under the scroll lock.
    const contentRef = React.useRef<HTMLElement | null>(null)
    const openerRef = React.useRef<HTMLElement | null>(null)

    return (
        <SheetPortal>
            <SheetOverlay />
            <DialogPrimitive.Content
                ref={(node) => {
                    contentRef.current = node
                    if (typeof ref === 'function') ref(node)
                    else if (ref) ref.current = node
                }}
                style={{ boxShadow: 'var(--shadow-flyout)' }}
                className={cn(sheetVariants({ size }), className)}
                onOpenAutoFocus={(e) => {
                    openerRef.current = document.activeElement as HTMLElement | null
                    onOpenAutoFocus?.(e)
                    if (e.defaultPrevented) return
                    e.preventDefault()
                    contentRef.current?.focus({ preventScroll: true })
                }}
                onCloseAutoFocus={(e) => {
                    onCloseAutoFocus?.(e)
                    if (e.defaultPrevented) return
                    e.preventDefault()
                    openerRef.current?.focus?.({ preventScroll: true })
                }}
                {...props}
            >
                {children}
            </DialogPrimitive.Content>
        </SheetPortal>
    )
})
SheetContent.displayName = 'SheetContent'

/**
 * Sheet header — title row with built-in close button.
 * Figma: 80px tall, padding 24, border-b border/subtle.
 */
const SheetHeader = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
    ({ className, children, ...props }, ref) => (
        <header
            ref={ref}
            className={cn(
                'flex items-start justify-between gap-3 px-6 py-5 border-b border-[var(--border-subtle)] shrink-0',
                className,
            )}
            {...props}
        >
            <div className="min-w-0 flex-1">{children}</div>
            <DialogPrimitive.Close
                className={cn(
                    'relative shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-md',
                    "before:absolute before:-inset-1.5 before:content-['']",
                    'text-[var(--icon-secondary)] hover:bg-[var(--bg-subtle)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-2',
                    'transition-colors',
                )}
                aria-label="Close panel"
            >
                <X className="h-4 w-4" aria-hidden="true" />
            </DialogPrimitive.Close>
        </header>
    ),
)
SheetHeader.displayName = 'SheetHeader'

const SheetTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn('font-serif text-xl font-medium text-[var(--text-primary)] leading-7 truncate', className)}
        {...props}
    />
))
SheetTitle.displayName = 'SheetTitle'

const SheetDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn('mt-1 text-sm text-[var(--text-secondary)] leading-5', className)}
        {...props}
    />
))
SheetDescription.displayName = 'SheetDescription'

/**
 * Sheet body — scrollable content region.
 * Figma: padding 24, vertical gap 16 between direct children.
 */
const SheetBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn('flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-4', className)}
            {...props}
        />
    ),
)
SheetBody.displayName = 'SheetBody'

/**
 * Sheet footer — right-aligned action row.
 * Figma: 72px tall, padding 16/24, gap 8, border-t border/subtle.
 */
const SheetFooter = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
    ({ className, ...props }, ref) => (
        <footer
            ref={ref}
            className={cn(
                'flex items-center justify-end gap-2 px-6 py-4 border-t border-[var(--border-subtle)] shrink-0',
                className,
            )}
            {...props}
        />
    ),
)
SheetFooter.displayName = 'SheetFooter'

export {
    Sheet,
    SheetTrigger,
    SheetClose,
    SheetPortal,
    SheetOverlay,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetBody,
    SheetFooter,
}
